import React, { useState, useEffect, useCallback } from 'react';
import {
  Save,
  Upload,
  Bot,
  AlertCircle,
  Check,
  ChevronDown
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import chatAgentService, { 
  CoreSettingDto, HelperLLMDto,
  HelperKnowledgeBaseDto 
} from './ChatIntents/chatAgentService';

// Type Definitions
export type Theme = 'dark' | 'light';
export type AgentType = 'Basic' | 'Advanced';

export interface AgentSettings {
  id?: string;
  agentId: string;
  title: string;
  description: string;
  agentType: AgentType;
  languages: string[];
  icon: string | null;
  llmModel: string;
  chatType: string;
  foUrl: string;
  instructions: string;
}

export interface CoreSettingsProps {
  theme?: Theme;
  existingAgentId?: string; // For edit mode
  mode?: 'create' | 'edit';
  onAgentTypeChange?: (agentType: AgentType) => void;
}

// Theme Configuration
const THEME_CONFIG: Record<Theme, any> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-800',
    input: 'bg-neutral-700 border-neutral-600 text-white'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-700',
      muted: 'text-gray-500'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900'
  }
};

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' }
];

// Language Selector Component
const LanguageSelector: React.FC<{
  selectedLanguages: string[];
  onLanguageToggle: (code: string) => void;
  theme: Theme;
}> = ({ selectedLanguages, onLanguageToggle, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const colors = THEME_CONFIG[theme];
  const hasError = selectedLanguages.length === 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full ${colors.input} border rounded-md px-3 py-2 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm ${
          hasError ? 'border-red-500' : ''
        }`}
      >
        <span>
          {selectedLanguages.length === 0
            ? 'Select languages...'
            : `${selectedLanguages.length} language(s) selected`
          }
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute z-10 w-full mt-1 ${colors.surface} border ${colors.border} rounded-md shadow-lg max-h-60 overflow-auto`}>
          {AVAILABLE_LANGUAGES.map((lang) => (
            <label
              key={lang.code}
              className={`flex items-center px-3 py-2 ${colors.hover} cursor-pointer`}
            >
              <input
                type="checkbox"
                checked={selectedLanguages.includes(lang.code)}
                onChange={() => onLanguageToggle(lang.code)}
                className="rounded border-gray-300 text-indigo-600 mr-3"
              />
              <span className="text-lg mr-2">{lang.flag}</span>
              <span className={`text-sm ${colors.text.primary}`}>{lang.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// Icon Upload Component
const IconUpload: React.FC<{
  currentIcon: string | null;
  onIconChange: (icon: string | null) => void;
  theme: Theme;
  hasError?: boolean;
}> = ({ currentIcon, onIconChange, theme, hasError = false }) => {
  const colors = THEME_CONFIG[theme];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onIconChange(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`h-12 w-12 flex-shrink-0 ${colors.secondary} border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden ${
        hasError ? 'border-red-500' : colors.border
      }`}>
        {currentIcon ? (
          <img src={currentIcon} alt="Agent Icon" className="w-full h-full object-cover" />
        ) : (
          <Bot className={`h-6 w-6 ${colors.text.muted}`} />
        )}
      </div>

      <div className="flex-1">
        <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 ${colors.secondary} border ${colors.border} rounded-md text-sm ${colors.text.secondary} hover:${colors.hover.split(' ')[0].replace('hover:', '')} transition-colors`}>
          <Upload className="w-4 h-4" />
          Upload Icon
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
        {currentIcon && (
          <button
            type="button"
            onClick={() => onIconChange(null)}
            className={`ml-2 px-2 py-1 text-xs ${colors.text.muted} hover:text-red-500 transition-colors`}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

// Main Component
const CoreSettings: React.FC<CoreSettingsProps> = ({
  theme = 'dark',
  existingAgentId,
  mode = 'create',
  onAgentTypeChange
}) => {
  const colors = THEME_CONFIG[theme];
  const navigate = useNavigate();
  const { chatbotId } = useParams<{ chatbotId?: string }>();
  
  // Use chatbotId from route params if existingAgentId is not provided
  const agentId = existingAgentId || chatbotId;

  const [settings, setSettings] = useState<AgentSettings>({
    agentId: 'agent_' + Math.random().toString(36).substr(2, 9),
    title: '',
    description: '',
    agentType: 'Basic',
    languages: ['en'],
    icon: null,
    llmModel: '',
    chatType: 'Text',
    foUrl: '',
    instructions: ''
  });

  const [availableLLMs, setAvailableLLMs] = useState<HelperLLMDto[]>([]);
  const [isLoadingLLMs, setIsLoadingLLMs] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoadingAgent, setIsLoadingAgent] = useState(false);

  // Load available LLMs on component mount
  useEffect(() => {
    loadAvailableLLMs();
  }, []);

  // Load existing agent data if in edit mode or if chatbotId is in route
  useEffect(() => {
    // Load agent if we have an agentId and we're in edit mode or chatbotId is in route
    if (agentId && (mode === 'edit' || chatbotId)) {
      loadExistingAgent(agentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, agentId, chatbotId]);

  const loadAvailableLLMs = async () => {
    try {
      setIsLoadingLLMs(true);
      const response = await chatAgentService.getActiveLLMs(); 
      if (response.success && response.data) {
        setAvailableLLMs(response.data); 
        // Set default LLM if none selected
        if (!settings.llmModel && response.data.length > 0) {
          const defaultLLM = response.data.find((llm: HelperLLMDto) => llm.isDefault) || response.data[0];
          setSettings(prev => ({ ...prev, llmModel: defaultLLM.id }));
        }
      }
    } catch (error) {
      console.error('Error loading LLMs:', error);
      setSaveError('Failed to load available LLM models');
    } finally {
      setIsLoadingLLMs(false);
    }
  };

  const loadExistingAgent = async (agentId: string) => {
    try {
      setIsLoadingAgent(true);
      setSaveError(null);
      const response = await chatAgentService.getAgentById(agentId);
      
      console.log('=== Load Agent Response ===', response);
      
      // Handle response structure: { success: true, data: { ... } }
      if (response?.success && response?.data) {
        const agentData = response.data;
        const loadedType: AgentType =
          typeof agentData.agent_type === 'string' &&
          agentData.agent_type.toLowerCase() === 'advanced'
            ? 'Advanced'
            : 'Basic';
        setSettings({
          id: agentData.id,
          agentId: agentData.agent_id,
          title: agentData.agent_title || '',
          description: agentData.description || '',
          agentType: loadedType,
          languages: agentData.languages || ['en'], // Default to ['en'] if not in response
          icon: agentData.agent_icon_url || null,
          llmModel: agentData.language_model || '',
          chatType: 'Text',
          foUrl: agentData.front_office_url || '', // Default to '' if not in response
          instructions: agentData.instructions || ''
        });
        onAgentTypeChange?.(loadedType);
        console.log('=== Agent Data Loaded ===', agentData);
      } else {
        setSaveError('Invalid response format from server');
      }
    } catch (error: any) {
      console.error('Error loading agent:', error);
      setSaveError(error.response?.data?.message || error.message || 'Failed to load agent data');
    } finally {
      setIsLoadingAgent(false);
    }
  };

  const handleInputChange = (field: keyof AgentSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError(null);
    if (field === 'agentType') {
      onAgentTypeChange?.(value as AgentType);
    }
  };

  const handleLanguageToggle = (languageCode: string) => {
    setSettings(prev => ({
      ...prev,
      languages: prev.languages.includes(languageCode)
        ? prev.languages.filter(code => code !== languageCode)
        : [...prev.languages, languageCode]
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const payload: CoreSettingDto = {
        id: settings.id,
        agent_id: settings.agentId,
        agent_title: settings.title,
        description: settings.description,
        agent_type: settings.agentType,
        chat_type: settings.chatType,
        language_model: settings.llmModel,
        agent_icon_url: settings.icon || undefined,
        languages: settings.languages,
        instructions: settings.instructions
      };

      console.log('=== Saving Core Settings ===', payload);

      const response = await chatAgentService.upsertCoreSetting(payload);

      console.log('=== Save Response ===', response);

      if (response.success) {
        console.log('Agent saved successfully:', response.data);
        setSaveSuccess(true); 
        const agentId = response.data?.id ;
        if (agentId) {
          setTimeout(() => {
            // Use window.location to force a full page refresh
            window.location.href = `/portal/chatbot/${agentId}`;
          }, 1000);
        }
      } else {
        setSaveError(response.data?.message || 'Failed to save agent settings');
      }

    } catch (error: any) {
      console.error('Error saving agent:', error);
      setSaveError(error.response?.data?.message || error.message || 'Failed to save agent settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Validate all required fields
  const isFormValid = 
    settings.title.trim() !== '' &&
    settings.description.trim() !== '' &&
    (settings.agentType === 'Basic' || settings.agentType === 'Advanced') &&
    settings.chatType.trim() !== '' &&
    settings.languages.length > 0 &&
    settings.icon !== null &&
    settings.llmModel.trim() !== '';

  if (isLoadingAgent) {
    return (
      <div className={`h-full ${colors.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={colors.text.secondary}>Loading agent data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${colors.background} overflow-auto`}>
      {/* Header */}
      <div className={`px-4 py-3 sm:px-6 border-b ${colors.border} ${colors.primary} sticky top-0 z-10`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-lg font-semibold ${colors.text.primary}`}>
              General Settings
            </h1>
            <p className={`text-sm ${colors.text.muted} mt-1`}>
              Configure your agent's basic parameters and behavior
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {saveSuccess && (
              <div className="flex items-center gap-2 text-green-500 text-sm">
                <Check className="w-4 h-4" />
                Saved successfully
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={!isFormValid || isSaving || isLoadingLLMs}
              className={`inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition-colors sm:w-auto ${colors.accent} hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-500`}
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="mx-auto max-w-4xl p-4 sm:p-5">

        {/* Error Message */}
        {saveError && (
          <div className={`mb-4 p-3 ${theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-700'} border rounded-md flex items-start gap-3`}>
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-red-800' : 'text-red-200'}`}>
                Error saving agent
              </p>
              <p className={`text-xs ${theme === 'light' ? 'text-red-700' : 'text-red-300'} mt-1`}>
                {saveError}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <section className={`rounded-lg border p-4 ${colors.surface} ${colors.border}`}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className={`text-sm font-semibold ${colors.text.primary}`}>Agent details</h2>
              {(mode === 'edit' || agentId) && (
                <div className={`text-xs ${colors.text.muted}`}>
                  Agent ID: <span className={`font-mono ${colors.text.secondary}`}>{settings.agentId}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2">
              <div>
                <label htmlFor="agent-title" className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Agent Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="agent-title"
                  type="text"
                  value={settings.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`min-h-10 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.input} ${
                    !settings.title.trim() ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter agent title"
                  maxLength={100}
                  required
                />
                <p className={`mt-1 text-xs ${colors.text.muted}`}>{settings.title.length}/100 characters</p>
              </div>

              <div>
                <label htmlFor="chat-type" className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Chat Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="chat-type"
                  value={settings.chatType}
                  onChange={(e) => handleInputChange('chatType', e.target.value)}
                  className={`min-h-10 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.input} ${
                    !settings.chatType ? 'border-red-500' : ''
                  }`}
                  required
                >
                  <option value="Text">Text</option>
                  <option value="Voice" disabled>Voice (coming soon)</option>
                  <option value="Multimodal" disabled>Multimodal (coming soon)</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="agent-description" className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="agent-description"
                  value={settings.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className={`w-full resize-y rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.input} ${
                    !settings.description.trim() ? 'border-red-500' : ''
                  }`}
                  placeholder="Describe what your agent does"
                  maxLength={500}
                  required
                />
                <p className={`mt-1 text-xs ${colors.text.muted}`}>{settings.description.length}/500 characters</p>
              </div>
            </div>
          </section>

          <section className={`rounded-lg border p-4 ${colors.surface} ${colors.border}`}>
            <h2 className={`mb-4 text-sm font-semibold ${colors.text.primary}`}>Capabilities</h2>

            <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <span className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Agent Type <span className="text-red-500">*</span>
                </span>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(['Basic', 'Advanced'] as AgentType[]).map((agentType) => {
                    const isSelected = settings.agentType === agentType;
                    return (
                      <button
                        key={agentType}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => handleInputChange('agentType', agentType)}
                        className={`min-h-[56px] rounded-md border px-3 py-2 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          isSelected
                            ? `${colors.accent} border-indigo-600 text-white`
                            : `${colors.surface} ${colors.text.secondary} ${colors.border}`
                        }`}
                      >
                        <div className="font-medium">{agentType}</div>
                        <div className={`mt-1 text-xs ${isSelected ? 'text-indigo-100' : colors.text.muted}`}>
                          {agentType === 'Basic' ? 'Simple conversations' : 'Complex workflows'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Languages <span className="text-red-500">*</span>
                </label>
                <LanguageSelector
                  selectedLanguages={settings.languages}
                  onLanguageToggle={handleLanguageToggle}
                  theme={theme}
                />
                <p className={`mt-1 text-xs ${colors.text.muted}`}>
                  {settings.languages.length} language(s) selected
                </p>
              </div>

              <div>
                <label htmlFor="language-model" className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Language Model (LLM) <span className="text-red-500">*</span>
                </label>
                {isLoadingLLMs ? (
                  <div className={`flex min-h-10 w-full items-center gap-2 rounded-md border px-3 py-2 text-sm ${colors.input}`}>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent"></div>
                    <span>Loading models...</span>
                  </div>
                ) : (
                  <>
                    <select
                      id="language-model"
                      value={settings.llmModel}
                      onChange={(e) => handleInputChange('llmModel', e.target.value)}
                      className={`min-h-10 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.input} ${
                        !settings.llmModel ? 'border-red-500' : ''
                      }`}
                      required
                    >
                      <option value="">Select a model...</option>
                      {availableLLMs.map((llm) => (
                        <option key={llm.id} value={llm.id}>
                          {llm.modelName} {llm.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    {settings.llmModel && (
                      <p className={`mt-1 text-xs ${colors.text.muted}`}>
                        {availableLLMs.find(llm => llm.id === settings.llmModel)?.costPreference || ''}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="md:col-span-2">
                <label className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
                  Agent Icon <span className="text-red-500">*</span>
                </label>
                <IconUpload
                  currentIcon={settings.icon}
                  onIconChange={(icon) => handleInputChange('icon', icon)}
                  theme={theme}
                  hasError={settings.icon === null}
                />
                <p className={`mt-1 text-xs ${colors.text.muted}`}>Upload a custom icon (required)</p>
              </div>
            </div>
          </section>

          <section className={`rounded-lg border p-4 ${colors.surface} ${colors.border}`}>
            <h2 className={`mb-4 text-sm font-semibold ${colors.text.primary}`}>Agent behavior</h2>
            <label htmlFor="agent-instructions" className={`mb-2 block text-sm font-medium ${colors.text.primary}`}>
              Agent Instructions
            </label>
            <textarea
              id="agent-instructions"
              value={settings.instructions}
              onChange={(e) => handleInputChange('instructions', e.target.value)}
              rows={4}
              className={`w-full resize-y rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.input}`}
              placeholder="Provide system-level instructions for how the agent should behave and respond"
              maxLength={4000}
            />
            <p className={`mt-1 text-xs ${colors.text.muted}`}>{settings.instructions.length}/4000 characters</p>
          </section>
        </div>

        {/* Validation Message */}
        {!isFormValid && (
          <div className={`mt-4 flex items-center gap-2 rounded-md border px-3 py-2 ${theme === 'light' ? 'bg-amber-50 border-amber-200' : 'bg-amber-900/20 border-amber-700'}`}>
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-500" />
            <p className={`text-xs ${theme === 'light' ? 'text-amber-800' : 'text-amber-200'}`}>
              Complete the highlighted required fields to enable saving.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoreSettings;

// import React, { useState } from 'react';
// import {
//   Save,
//   Upload,
//   Bot,
//   AlertCircle,
//   Check,
//   ChevronDown
// } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';

// // Type Definitions
// export type Theme = 'dark' | 'light';
// export type AgentType = 'basic' | 'advanced';

// export interface AgentSettings {
//   agentId: string;
//   title: string;
//   description: string;
//   role: string;
//   agentType: AgentType;
//   languages: string[];
//   icon: string | null;
//   llmModel: string;
//   chatType: string;
//   foUrl: string;
// }

// export interface CoreSettingsProps {
//   theme?: Theme;
//   onSave?: (settings: AgentSettings) => Promise<{ success: boolean; data?: any; error?: string }>;
//   tenantId?: string;
//   userId?: string;
// }

// // Theme Configuration
// const THEME_CONFIG: Record<Theme, any> = {
//   dark: {
//     primary: 'bg-neutral-900',
//     secondary: 'bg-neutral-800',
//     background: 'bg-neutral-950',
//     surface: 'bg-neutral-800',
//     border: 'border-neutral-700',
//     text: {
//       primary: 'text-white',
//       secondary: 'text-neutral-300',
//       muted: 'text-neutral-400'
//     },
//     accent: 'bg-indigo-600',
//     hover: 'hover:bg-neutral-800',
//     input: 'bg-neutral-700 border-neutral-600 text-white'
//   },
//   light: {
//     primary: 'bg-white',
//     secondary: 'bg-gray-50',
//     background: 'bg-gray-100',
//     surface: 'bg-white',
//     border: 'border-gray-200',
//     text: {
//       primary: 'text-gray-900',
//       secondary: 'text-gray-700',
//       muted: 'text-gray-500'
//     },
//     accent: 'bg-indigo-600',
//     hover: 'hover:bg-gray-100',
//     input: 'bg-white border-gray-300 text-gray-900'
//   }
// };

// const AVAILABLE_LANGUAGES = [
//   { code: 'en', name: 'English', flag: '🇺🇸' },
//   { code: 'es', name: 'Spanish', flag: '🇪🇸' },
//   { code: 'fr', name: 'French', flag: '🇫🇷' },
//   { code: 'de', name: 'German', flag: '🇩🇪' },
//   { code: 'it', name: 'Italian', flag: '🇮🇹' },
//   { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
//   { code: 'ru', name: 'Russian', flag: '🇷🇺' },
//   { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
//   { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
//   { code: 'ko', name: 'Korean', flag: '🇰🇷' },
//   { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
//   { code: 'hi', name: 'Hindi', flag: '🇮🇳' }
// ];

// const AVAILABLE_LLMS = [
//   { id: 'b6e0c3a0-9f2d-4e1d-9b63-6c9b048f2a13', name: 'GPT-4', provider: 'OpenAI', description: 'Most capable GPT model' },
//   { id: 'c7f29d8f-18a2-4c0d-b36d-47f6b3e91e0a', name: 'GPT-3.5 Turbo', provider: 'OpenAI', description: 'Fast and efficient' },
//   { id: 'a11c1f85-51f6-4e93-87e0-df3446fdad2a', name: 'Claude 3 Opus', provider: 'Anthropic', description: 'Most powerful Claude model' },
//   { id: '7d65eaa2-334b-4a64-bac7-b9f47c19cbbf', name: 'Claude 3 Sonnet', provider: 'Anthropic', description: 'Balanced performance' },
//   { id: '95fdc4ac-6f7c-4f56-bf2c-37336bc82367', name: 'Claude 3 Haiku', provider: 'Anthropic', description: 'Fastest Claude model' },
//   { id: 'e33f6c51-27f3-4d0b-b6fc-b89e1e8f9e6b', name: 'Gemini Pro', provider: 'Google', description: "Google's advanced model" },
//   { id: 'f4a1d6a2-984b-4e56-933d-b0d693ef5c8f', name: 'Llama 2 70B', provider: 'Meta', description: 'Open source model' }
// ];


// // Language Selector Component
// const LanguageSelector: React.FC<{
//   selectedLanguages: string[];
//   onLanguageToggle: (code: string) => void;
//   theme: Theme;
// }> = ({ selectedLanguages, onLanguageToggle, theme }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const colors = THEME_CONFIG[theme];

//   return (
//     <div className="relative">
//       <button
//         type="button"
//         onClick={() => setIsOpen(!isOpen)}
//         className={`w-full ${colors.input} border rounded-md px-3 py-2 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm`}
//       >
//         <span>
//           {selectedLanguages.length === 0
//             ? 'Select languages...'
//             : `${selectedLanguages.length} language(s) selected`
//           }
//         </span>
//         <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
//       </button>

//       {isOpen && (
//         <div className={`absolute z-10 w-full mt-1 ${colors.surface} border ${colors.border} rounded-md shadow-lg max-h-60 overflow-auto`}>
//           {AVAILABLE_LANGUAGES.map((lang) => (
//             <label
//               key={lang.code}
//               className={`flex items-center px-3 py-2 ${colors.hover} cursor-pointer`}
//             >
//               <input
//                 type="checkbox"
//                 checked={selectedLanguages.includes(lang.code)}
//                 onChange={() => onLanguageToggle(lang.code)}
//                 className="rounded border-gray-300 text-indigo-600 mr-3"
//               />
//               <span className="text-lg mr-2">{lang.flag}</span>
//               <span className={`text-sm ${colors.text.primary}`}>{lang.name}</span>
//             </label>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// // Icon Upload Component
// const IconUpload: React.FC<{
//   currentIcon: string | null;
//   onIconChange: (icon: string | null) => void;
//   theme: Theme;
// }> = ({ currentIcon, onIconChange, theme }) => {
//   const colors = THEME_CONFIG[theme];

//   const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const file = event.target.files?.[0];
//     if (file) {
//       const reader = new FileReader();
//       reader.onload = (e) => {
//         onIconChange(e.target?.result as string);
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   return (
//     <div className="flex items-center gap-4">
//       <div className={`w-16 h-16 ${colors.secondary} border-2 border-dashed ${colors.border} rounded-lg flex items-center justify-center overflow-hidden`}>
//         {currentIcon ? (
//           <img src={currentIcon} alt="Agent Icon" className="w-full h-full object-cover" />
//         ) : (
//           <Bot className={`w-8 h-8 ${colors.text.muted}`} />
//         )}
//       </div>

//       <div className="flex-1">
//         <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 ${colors.secondary} border ${colors.border} rounded-md text-sm ${colors.text.secondary} hover:${colors.hover.split(' ')[0].replace('hover:', '')} transition-colors`}>
//           <Upload className="w-4 h-4" />
//           Upload Icon
//           <input
//             type="file"
//             accept="image/*"
//             onChange={handleFileUpload}
//             className="hidden"
//           />
//         </label>
//         {currentIcon && (
//           <button
//             type="button"
//             onClick={() => onIconChange(null)}
//             className={`ml-2 px-2 py-1 text-xs ${colors.text.muted} hover:text-red-500 transition-colors`}
//           >
//             Remove
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// // Main Component
// const CoreSettings: React.FC<CoreSettingsProps> = ({
//   theme = 'dark',
//   onSave,
//   tenantId,
//   userId
// }) => {
//   const colors = THEME_CONFIG[theme];

//   const [settings, setSettings] = useState<AgentSettings>({
//     agentId: 'agent_' + Math.random().toString(36).substr(2, 9),
//     title: '',
//     description: '',
//     role: '',
//     agentType: 'basic',
//     languages: ['en'],
//     icon: null,
//     llmModel: 'gpt-3.5-turbo',
//     chatType: 'text',
//     foUrl: ''
//   });

//   const [isSaving, setIsSaving] = useState(false);
//   const [saveSuccess, setSaveSuccess] = useState(false);
//   const [saveError, setSaveError] = useState<string | null>(null);

//   const handleInputChange = (field: keyof AgentSettings, value: any) => {
//     setSettings(prev => ({ ...prev, [field]: value }));
//     setSaveSuccess(false);
//     setSaveError(null);
//   };

//   const handleLanguageToggle = (languageCode: string) => {
//     setSettings(prev => ({
//       ...prev,
//       languages: prev.languages.includes(languageCode)
//         ? prev.languages.filter(code => code !== languageCode)
//         : [...prev.languages, languageCode]
//     }));
//   };

//   const navigate = useNavigate();
//   const handleSave = async () => {
//     setIsSaving(true);
//     setSaveError(null);

//     try {
//       console.log('=== CoreSettings: Calling parent onSave ===', settings);

//       if (!onSave) {
//         throw new Error('onSave handler not provided');
//       }

//       const result = await onSave(settings);

//       console.log('=== CoreSettings: Result from parent ===', result);

//       if (result?.success) {
//         console.log('Agent created successfully:', result.data);
//         setSaveSuccess(true);
//         navigate(`/portal/chatagent`);
//         setTimeout(() => setSaveSuccess(false), 3000);
//       } else {
//         setSaveError(result?.error || 'Failed to create chat agent');
//       }

//     } catch (error: any) {
//       console.error('Error saving agent:', error);
//       setSaveError(error.message || 'Failed to create chat agent');
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   const isFormValid = settings.title && settings.role && settings.languages.length > 0;

//   return (
//     <div className={`h-full ${colors.background} overflow-auto`}>
//       {/* Header */}
//       <div className={`px-6 py-4 border-b ${colors.border} ${colors.primary} sticky top-0 z-10`}>
//         <div className="flex items-center justify-between">
//           <div>
//             <h1 className={`text-lg font-semibold ${colors.text.primary}`}>Core Settings</h1>
//             <p className={`text-sm ${colors.text.muted} mt-1`}>Configure your agent's basic parameters and behavior</p>
//           </div>

//           <div className="flex items-center gap-3">
//             {saveSuccess && (
//               <div className="flex items-center gap-2 text-green-500 text-sm">
//                 <Check className="w-4 h-4" />
//                 Saved successfully
//               </div>
//             )}

//             <button
//               onClick={handleSave}
//               disabled={!isFormValid || isSaving}
//               className={`px-4 py-2 ${colors.accent} hover:bg-indigo-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors flex items-center gap-2`}
//             >
//               {isSaving ? (
//                 <>
//                   <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   Saving...
//                 </>
//               ) : (
//                 <>
//                   <Save className="w-4 h-4" />
//                   Save Settings
//                 </>
//               )}
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Form Content */}
//       <div className="p-6 max-w-4xl mx-auto">

//         {/* Error Message */}
//         {saveError && (
//           <div className={`mb-6 p-4 ${theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-700'} border rounded-md flex items-start gap-3`}>
//             <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
//             <div>
//               <p className={`text-sm font-medium ${theme === 'light' ? 'text-red-800' : 'text-red-200'}`}>
//                 Error saving agent
//               </p>
//               <p className={`text-xs ${theme === 'light' ? 'text-red-700' : 'text-red-300'} mt-1`}>
//                 {saveError}
//               </p>
//             </div>
//           </div>
//         )}

//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

//           {/* Left Column */}
//           <div className="space-y-6">

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Agent ID
//               </label>
//               <input
//                 type="text"
//                 value={settings.agentId}
//                 disabled
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm opacity-60 cursor-not-allowed`}
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>Automatically generated unique identifier</p>
//             </div>

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Agent Title <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 value={settings.title}
//                 onChange={(e) => handleInputChange('title', e.target.value)}
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
//                 placeholder="Enter agent title"
//                 maxLength={100}
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>{settings.title.length}/100 characters</p>
//             </div>

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Description
//               </label>
//               <textarea
//                 value={settings.description}
//                 onChange={(e) => handleInputChange('description', e.target.value)}
//                 rows={4}
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none`}
//                 placeholder="Describe what your agent does"
//                 maxLength={500}
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>{settings.description.length}/500 characters</p>
//             </div>

//             {/* <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Agent Role <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 value={settings.role}
//                 onChange={(e) => handleInputChange('role', e.target.value)}
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
//                 placeholder="e.g., Customer Support Assistant"
//                 maxLength={100}
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>Define the primary role of your agent</p>
//             </div> */}

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Chat Type
//               </label>
//               <select
//                 value={settings.chatType}
//                 onChange={(e) => handleInputChange('chatType', e.target.value)}
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
//               >
//                 <option value="text">Text</option>
//                 <option value="voice">Voice</option>
//                 <option value="multimodal">Multimodal</option>
//               </select>
//             </div>

//           </div>

//           {/* Right Column */}
//           <div className="space-y-6">

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Agent Type
//               </label>
//               <div className="grid grid-cols-2 gap-3">
//                 <button
//                   type="button"
//                   onClick={() => handleInputChange('agentType', 'basic')}
//                   className={`px-4 py-3 border rounded-md text-sm transition-colors ${settings.agentType === 'basic'
//                       ? `${colors.accent} text-white border-indigo-600`
//                       : `${colors.surface} ${colors.text.secondary} ${colors.border}`
//                     }`}
//                 >
//                   <div className="font-medium">Basic</div>
//                   <div className={`text-xs mt-1 ${settings.agentType === 'basic' ? 'text-indigo-100' : colors.text.muted}`}>
//                     Simple conversations
//                   </div>
//                 </button>

//                 <button
//                   type="button"
//                   onClick={() => handleInputChange('agentType', 'advanced')}
//                   className={`px-4 py-3 border rounded-md text-sm transition-colors ${settings.agentType === 'advanced'
//                       ? `${colors.accent} text-white border-indigo-600`
//                       : `${colors.surface} ${colors.text.secondary} ${colors.border}`
//                     }`}
//                 >
//                   <div className="font-medium">Advanced</div>
//                   <div className={`text-xs mt-1 ${settings.agentType === 'advanced' ? 'text-indigo-100' : colors.text.muted}`}>
//                     Complex workflows
//                   </div>
//                 </button>
//               </div>
//             </div>

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Languages <span className="text-red-500">*</span>
//               </label>
//               <LanguageSelector
//                 selectedLanguages={settings.languages}
//                 onLanguageToggle={handleLanguageToggle}
//                 theme={theme}
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>
//                 {settings.languages.length} language(s) selected
//               </p>
//             </div>

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Agent Icon
//               </label>
//               <IconUpload
//                 currentIcon={settings.icon}
//                 onIconChange={(icon) => handleInputChange('icon', icon)}
//                 theme={theme}
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>Upload a custom icon (optional)</p>
//             </div>

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Language Model (LLM)
//               </label>
//               <select
//                 value={settings.llmModel}
//                 onChange={(e) => handleInputChange('llmModel', e.target.value)}
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
//               >
//                 {AVAILABLE_LLMS.map((llm) => (
//                   <option key={llm.id} value={llm.id}>
//                     {llm.name} - {llm.provider}
//                   </option>
//                 ))}
//               </select>
//               <p className={`text-xs ${colors.text.muted} mt-1`}>
//                 {AVAILABLE_LLMS.find(llm => llm.id === settings.llmModel)?.description}
//               </p>
//             </div>

//             <div>
//               <label className={`block text-sm font-medium ${colors.text.primary} mb-2`}>
//                 Front Office URL
//               </label>
//               <input
//                 type="text"
//                 value={settings.foUrl}
//                 onChange={(e) => handleInputChange('foUrl', e.target.value)}
//                 className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
//                 placeholder="https://example.com/chat"
//               />
//               <p className={`text-xs ${colors.text.muted} mt-1`}>URL where agent will be accessible (optional)</p>
//             </div>

//           </div>
//         </div>

//         {/* Validation Message */}
//         {!isFormValid && (
//           <div className={`mt-6 p-4 ${theme === 'light' ? 'bg-amber-50 border-amber-200' : 'bg-amber-900/20 border-amber-700'} border rounded-md flex items-start gap-3`}>
//             <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
//             <div>
//               <p className={`text-sm font-medium ${theme === 'light' ? 'text-amber-800' : 'text-amber-200'}`}>
//                 Please complete the required fields
//               </p>
//               <ul className={`text-xs ${theme === 'light' ? 'text-amber-700' : 'text-amber-300'} mt-1 space-y-1`}>
//                 {!settings.title && <li>• Agent title is required</li>}
//                 {!settings.role && <li>• Agent role is required</li>}
//                 {settings.languages.length === 0 && <li>• At least one language must be selected</li>}
//               </ul>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default CoreSettings;